// @ts-nocheck
import * as turf from '@turf/turf';
import React, { useEffect } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { useDidUpdate } from 'sonata-react-components';

import {
  getCampaignsZones,
  getMultipleGeoJSONData,
  getReportingStreamStatsUserId,
  getZonesWithBoundingBox
} from '@/modules/reporting/reporting.resources';
import { reportingToggleMapLoading } from '@/store/actions/reporting.actions';
import { getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getAdvancedFilters, getCampaigns, getCurrency } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { MultiCountryLevelDataLayer } from '@/ts-modules/reporting/Map/Layers';
import { SonataZone } from '@/ts-types/zones.types';

const LOCATIONS_SOURCE_ID = 'campaignLocations';
const LOCATIONS_LAYER_ID = 'layer_campaignLocations';

type Location = SonataZone & {
  [key: string]: any;
  boundingBox?: number[];
  polygon?: turf.Geometry;
};

type GroupedLocations = {
  cartographyPolygons: Location[];
  circles: Location[];
  mercatorPolygons: Location[];
  polygons: Location[];
};

type Props = {
  mapId: string;
  opacity?: number;
  section?: string;
};

const LocationsLayer: React.VFC<Props> = ({ mapId, opacity = 0.8, section = 'general' }) => {
  const dispatch = useDispatch();
  const tokens = {
    campaignsZones: {},
    locationsStats: {}
  };
  const { getMap, getMapCartographyFilter, getMapEvents, getMapLocationsData, setMapSettings } = useReportingMap();
  const { settings } = getMap({ id: mapId })!;
  const cartographySettings = {
    ...settings.cartography,
    status: settings.cartography.status || 'idle'
  };
  const selectedCampaignIds: string[] = useSelector(getCampaigns);
  const reportingCampaigns: Record<string, any>[] = useSelector(getSelectedCampaigns);
  // @ts-ignore
  const generalFilters: Record<string, any> = useSelector(store => getAdvancedFilters(store, section));
  // @ts-ignore
  const currentCurrency: Record<string, any> = useSelector(store => getCurrency(store, section));
  const user: Record<string, any> = useSelector(getCurrentUser);

  const {
    cartographyLevel: generalSectionCartographyLevel,
    cartographyLevelIds: generalCartographyLevelIds,
    timeFrame,
    timeZone: timezone,
    variableType,
    variableValues
  } = generalFilters;

  const cartographyFilter = getMapCartographyFilter({ id: mapId });
  const { level: cartographyLevel, shapeIds: cartographyLevelIds } = cartographyFilter!;
  const mapCartographyLevel = cartographyLevel || generalSectionCartographyLevel;
  const mapCartographyLevelIds = cartographyLevelIds.length ? cartographyLevelIds : generalCartographyLevelIds;
  const cartography = mapCartographyLevelIds.length ? { [mapCartographyLevel]: mapCartographyLevelIds } : undefined;
  const variable = variableType && variableValues.length ? { [variableType]: variableValues } : undefined;
  const campaigns = reportingCampaigns.filter(reportingCampaign => selectedCampaignIds.includes(reportingCampaign.id));
  const mapEvents = getMapEvents({ id: mapId });

  const locationStatsBody = {
    opts: {
      currencyOptions: {
        currency: currentCurrency
      }
    },
    params: {
      dateInterval: {
        timezone,
        ...timeFrame
      },
      filters: {
        ...(cartography && { cartography }),
        ...variable
      },
      groups: ['lid'],
      outputMetrics: mapEvents
    }
  };

  const groupLocations = (locations: Location[]): GroupedLocations =>
    locations
      .filter(
        ({ retargetingCandidate, retargetingType, smart, type: zoneType }) =>
          zoneType !== 'attributionTorus' && (!retargetingCandidate || retargetingType !== 'only') && !smart
      )
      .reduce(
        (accumulator, location) => {
          if (location.type === 'circle') {
            accumulator.circles.push(location);
            return accumulator;
          }
          if (location.cartographyLevel) {
            accumulator.cartographyPolygons.push(location);
            return accumulator;
          }
          if (location.geojson || location.polygon) {
            accumulator.polygons.push(location);
            return accumulator;
          }
          accumulator.mercatorPolygons.push(location);
          return accumulator;
        },
        {
          cartographyPolygons: [],
          circles: [],
          mercatorPolygons: [],
          polygons: []
        } as GroupedLocations
      );

  const getLocationsData = async () => {
    dispatch(reportingToggleMapLoading(mapId, true));
    const { cartographyPolygons, circles, mercatorPolygons, polygons } = groupLocations(
      (await getCampaignsZones(selectedCampaignIds)) as any
    );
    if (mercatorPolygons.length) {
      const geojsons: turf.Geometry[] = await getMultipleGeoJSONData(mercatorPolygons);
      geojsons.forEach((geojson, index) => {
        if (mercatorPolygons[index]) {
          mercatorPolygons[index].geojson = geojson;
        }
      });
    }
    const locationsItems = [
      ...((await getZonesWithBoundingBox(cartographyPolygons)) as Location[]).map(z => ({
        _id: z._id,
        boundingBox: z.boundingBox,
        cartographyLevel: z.cartographyLevel,
        cartographyLevelId: z.cartographyLevelId,
        country: z.country,
        name: z.name,
        polygon: null
      })),
      ...[...mercatorPolygons, ...polygons]
        .filter(z => !!z.geojson)
        .map(z => ({
          _id: z._id,
          name: z.name,
          polygon: (z.geojson as turf.Feature).geometry || z.geojson
        })),
      ...circles.map(z => ({
        _id: z._id,
        name: z.name,
        polygon: turf.circle(turf.point([z.latlng.lng, z.latlng.lat]), z.radius, { units: 'meters' }).geometry
      }))
    ];
    const locationsStats = await getReportingStreamStatsUserId(
      locationStatsBody,
      tokens.locationsStats,
      user.id,
      campaigns.map(campaign => campaign.id).join(','),
      'none',
      mapEvents
    );
    const locationsItemsFiltered = locationsItems.filter(({ _id }) =>
      locationsStats.map(({ lid }: { lid: string }) => lid).includes(_id)
    );
    dispatch(reportingToggleMapLoading(mapId, false));
    setMapSettings({
      id: mapId,
      value: {
        ...settings,
        cartography: {
          ...settings.cartography,
          status: 'idle'
        },
        locations: {
          ...settings.locations,
          data: {
            ...settings.locations.data,
            items: locationsItemsFiltered as any,
            stats: locationsStats
          }
        }
      }
    });
  };

  useDidUpdate(() => {
    if (cartographySettings.status === 'reload') {
      getLocationsData();
    }
  }, [cartographySettings.status]);

  useDidUpdate(() => {
    getLocationsData();
  }, [mapEvents]);

  useEffect(() => {
    getLocationsData();
  }, []);

  const { cartographyPolygons, polygons } = groupLocations(getMapLocationsData({ id: mapId }) as any);
  const sourceData = turf.featureCollection<turf.Geometry, { [name: string]: any } | null>(
    polygons.map(({ polygon, ...rest }) => turf.feature(polygon!, rest))
  );

  const locationsSourceId = `${mapId}_${LOCATIONS_SOURCE_ID}`;
  const locationsLayerId = `${mapId}_${LOCATIONS_LAYER_ID}`;

  return (
    <>
      {cartographyPolygons.length && (
        <MultiCountryLevelDataLayer
          id={`${mapId}_locations_multi_country_level`}
          indexLimits={{
            max: 100,
            min: 0
          }}
          layerMapData={
            cartographyPolygons as unknown as React.ComponentProps<typeof MultiCountryLevelDataLayer>['layerMapData']
          }
          promoteId="name"
          type="vector"
        />
      )}
      <Source data={sourceData as any} id={locationsSourceId} key={LOCATIONS_SOURCE_ID} type="geojson">
        <Layer
          id={locationsLayerId}
          key={locationsLayerId}
          paint={{
            'fill-color': ['get', 'color'],
            'fill-opacity': opacity
          }}
          type="fill"
        />
      </Source>
    </>
  );
};

export default LocationsLayer;
